'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from '@/tools/auth'
import { useErrorHandling } from '@/tools/o11y/client'
import type { PhoneNumber, UserModel } from '@/user/entities'
import { fetchUserData } from '@/user/use-cases'

export interface UserData {
  userData: UserModel | undefined
  setUserData: (newUserData: UserModel) => void
  setPhoneNumber: (phoneNumber: PhoneNumber) => void
}

export const UserDataContext = createContext<UserData>({
  userData: undefined,
  setPhoneNumber: () => {},
  setUserData: () => {
    // noop
  },
})

export const UserDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { execWithErrorTracking } = useErrorHandling()
  const [userData, setUserData] = useState<UserModel | undefined>()
  const { isLoggedIn } = useAuth()

  const setPhoneNumber = (phoneNumber: PhoneNumber) => {
    setUserData((prevUserData) => {
      if (!prevUserData) {
        return prevUserData
      }
      return {
        ...prevUserData,
        phoneNumber,
      }
    })
  }

  useEffect(() => {
    execWithErrorTracking(async () => {
      if (isLoggedIn) {
        const userData: UserModel = await fetchUserData()
        setUserData(userData)
      } else {
        setUserData(undefined)
      }
    })
  }, [isLoggedIn, execWithErrorTracking])

  return (
    <UserDataContext.Provider value={{ userData, setUserData, setPhoneNumber }}>
      {children}
    </UserDataContext.Provider>
  )
}

export const useUserData = () => useContext(UserDataContext)
